import { useShowcaseLanguage } from "./useShowcaseLanguage";
import { useEffect, useRef, useState } from "react";
import { MacIcon } from "src/shared/ui/src/design/MacIcon";
import "./planning-story.css";

const duration = 14;
const chapters = [
  {
    at: 0,
    label: "Groupe",
    caption: "Déplacez un groupe vers un autre plateau.",
  },
  {
    at: 5,
    label: "Déplier",
    caption: "Dépliez un autre groupe pour retrouver ses athlètes.",
  },
  {
    at: 8,
    label: "Athlète",
    caption: "Déplacez un athlète dans un autre groupe.",
  },
];
const points = [
  [0, 15, 40],
  [1.5, 4.96, 26.1],
  [2, 4.96, 26.1],
  [4, 15.87, 48.33],
  [5.7, 46.36, 25.8],
  [6, 46.36, 25.8],
  [7, 46.36, 30.33],
  [8.5, 5.56, 34.56],
  [9, 5.56, 34.56],
  [11, 15.87, 83.67],
  [12, 15, 56.5],
  [14, 15, 56.5],
];
function pointer(time: number) {
  const next = points.findIndex((p) => p[0] > time);
  if (next < 1) return points[points.length - 1].slice(1);
  const a = points[next - 1],
    b = points[next];
  const fraction = (time - a[0]) / (b[0] - a[0]);
  const eased = fraction * fraction * (3 - 2 * fraction);
  return [a[1] + (b[1] - a[1]) * eased, a[2] + (b[2] - a[2]) * eased];
}

/** Replay of screenshots captured in the real planning workspace, using demo data. */
export default function PlanningStory({
  theme,
  title,
  description,
}: {
  theme: "light" | "dark";
  title: string;
  description: string;
}) {
  const { t, assetBase } = useShowcaseLanguage();
  const root = useRef<HTMLElement>(null);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const motion = matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      setPlaying(!motion.matches);
      if (motion.matches) setTime(11);
    };
    update();
    motion.addEventListener("change", update);
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.15 },
    );
    if (root.current) observer.observe(root.current);
    return () => {
      observer.disconnect();
      motion.removeEventListener("change", update);
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setFailed(false);
    Promise.all(
      Array.from(
        { length: 5 },
        (_, frame) =>
          new Promise<void>((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve();
            image.onerror = reject;
            image.src = `${assetBase}planning-story/groups-${theme}-${frame}.jpg`;
          }),
      ),
    )
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [theme, assetBase]);
  useEffect(() => {
    if (!playing || !visible || !ready) return;
    let previous = performance.now();
    const timer = window.setInterval(() => {
      const now = performance.now();
      const elapsed = (now - previous) / 1000;
      previous = now;
      if (!document.hidden)
        setTime((value) => (value + Math.min(elapsed, 0.2)) % duration);
    }, 50);
    return () => clearInterval(timer);
  }, [playing, visible, ready]);
  const frame = time < 4 ? 0 : time < 6 ? 1 : time < 7 ? 2 : time < 11 ? 3 : 4;
  const chapter = time < 5 ? 0 : time < 8 ? 1 : 2;
  const groupDragging = time >= 2 && time < 4;
  const athleteDragging = time >= 9 && time < 11;
  const dragProgress = groupDragging ? (time - 2) / 2 : (time - 9) / 2;
  const [x, y] = pointer(time);
  return (
    <>
      <div className="journey-copy planning-copy">
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <figure
        className="planning-story"
        ref={root}
        aria-label={t("Démonstration du planning réel")}
      >
        <div className="planning-stage" aria-hidden="true">
          <div className="planning-canvas">
            <img
              src={`${assetBase}planning-story/groups-${theme}-${ready ? frame : 0}.jpg`}
              width="1512"
              height="900"
              alt=""
            />
            {(groupDragging || athleteDragging) && ready && (
              <div
                className="planning-drag-placeholder"
                style={{
                  left: groupDragging ? "3.7%" : "4.8%",
                  top: groupDragging ? "23.6%" : "32.5%",
                  width: groupDragging ? "44.1%" : "43%",
                  height: groupDragging ? "4.4%" : "4.1%",
                }}
              />
            )}
            {(groupDragging || athleteDragging) && ready && (
              <img
                className="planning-dragged"
                src={`${assetBase}planning-story/groups-${theme}-${groupDragging ? 0 : 3}.jpg`}
                width="1512"
                height="900"
                alt=""
                style={{
                  clipPath: groupDragging
                    ? "inset(23.6% 52.2% 72% 3.7% round 4px)"
                    : "inset(32.5% 52.2% 63.4% 4.8% round 4px)",
                  transform: `translate(${dragProgress * (groupDragging ? 10.91 : 10.31)}%, ${dragProgress * (groupDragging ? 22.23 : 49.11)}%)`,
                }}
              />
            )}
            {ready &&
              ((time >= 5.8 && time < 6.3) || (time >= 6.8 && time < 7.3)) && (
                <span
                  className="planning-click"
                  style={{ left: `${x}%`, top: `${y}%` }}
                />
              )}
            {ready && (
              <svg
                className="planning-pointer"
                viewBox="0 0 24 30"
                style={{ left: `${x}%`, top: `${y}%` }}
              >
                <path
                  d="m2 2 19 15-10 1-4 9Z"
                  fill="white"
                  stroke="#202124"
                  strokeWidth="1.5"
                />
              </svg>
            )}
          </div>
        </div>
        <div className="planning-controls">
          <button
            type="button"
            disabled={!ready}
            onClick={() => setPlaying(!playing)}
            aria-label={
              playing
                ? t("Mettre en pause le planning")
                : t("Lire l’animation du planning")
            }
          >
            <MacIcon name={playing ? "pause" : "play"} size={15} />
          </button>
          <button
            type="button"
            disabled={!ready}
            onClick={() => {
              setTime(0);
              setPlaying(true);
            }}
            aria-label={t("Rejouer le planning")}
          >
            <MacIcon name="undo" size={14} />
          </button>
          <input
            aria-label={t("Position dans l’animation du planning")}
            type="range"
            min="0"
            max={duration - 0.1}
            step="0.1"
            value={time}
            onChange={(event) => {
              setPlaying(false);
              setTime(Number(event.target.value));
            }}
          />
          <span>{`${String(Math.floor(time)).padStart(2, "0")} / 14 s`}</span>
        </div>
        <figcaption className="planning-source" role="status">
          {failed
            ? t(
                "Aperçu du planning indisponible. Rechargez la page pour lire la démonstration.",
              )
            : t(chapters[chapter].caption)}
          {t(" Application réelle, données de démonstration.")}
        </figcaption>
      </figure>
    </>
  );
}
